import {Validators} from '@angular/forms';

import {Attachment, AttachmentMeta} from '@atmi/core/models/cards/attachament';
import {Movement, MovementMeta} from '@atmi/core/models/cards/movement';
import {Relation} from '@atmi/core/models/cards/relation';
import {Restoration, RestorationMeta} from '@atmi/core/models/cards/restoration';
import {SourceArc, SourceArcMeta} from '@atmi/core/models/cards/source-arc';
import {SourceBib, SourceBibMeta} from '@atmi/core/models/cards/source-bib';
import {Status} from '@atmi/core/models/status';

import {FieldsOptions} from '@sim/form';

import {iccdDateValidator} from '@sim/form/validators';

export interface NCT {
  /** Numero catalogo generale */
  NCTR: string;
  /** Codice regione */
  NCTN: string;
}

export interface CMP {
  /** Data */
  CMPD: string | Date;
  /** Nome */
  CMPN: string;
}

export interface OGT {
  /** Definizione */
  OGTD: string;
}

export interface SGT {
  /** Titolo */
  SGTT: string;
  /** Identificazione */
  SGTI: string;
}

export interface DES {
  /** Indicazioni sull'oggetto */
  DESO: string;
}

export interface DTS {
  /** Da */
  DTSI: string;
  /** A */
  DTSF: string;
}

export interface DT {
  DTS: DTS;
  /** Motivazione cronologia */
  DTM: string[];
}

export interface AUT {
  /** Nome scelto */
  AUTN: string;
  /** Dati anagrafici */
  AUTA: string;
}

export interface ACQ {
  /** Tipo acquisizione */
  ACQT: string;
  /** Nome */
  ACQN: string;
  /** Data acquisizione */
  ACQD: string;
  /** Luogo acquisizione */
  ACQL: string;
}

export interface AGG {
  /** Data */
  AGGD: string;
  /** Nome */
  AGGN: string;
}

export interface CDG {
  /** Indicazione generica */
  CDGG: string;
  /** Indicazione specifica */
  CDGS: string;
  /** Indirizzo */
  CDGI: string;
}

export interface PRV {
  /** Stato */
  PRVS?: string;
  /** Regione */
  PRVR?: string;
  /** Provincia */
  PRVP?: string;
  /** Comune */
  PRVC?: string;
  /** Località */
  PRVL?: string;
  /** Altra ripartizione amministrativa o località estera */
  PRVE?: string;
}

export interface PRD {
  /** Data di ingresso */
  PRDI: string;
  /** Data di uscita */
  PRDU: string;
}

export interface LA {
  /** Tipo di localizzazione */
  TCL: string;
  /** Localizzazione Geografico Amministrativa */
  PRV: PRV;
  /** Data */
  PRD: PRD;
}

export interface STC {
  /** Stato di conservazione */
  STCC: string;
  /** Indicazioni specifiche */
  STCS: string;
}

export interface ICCD {
  /** Notizie storico-critiche */
  NSC: string;
  /** Tipo di scheda */
  TSK: 'OA' | 'S' | 'MI' | 'F';
  /** Livello di ricerca */
  LIR: '0' | '1' | '2' | '3' | '4' | '5';
  /** Ente schedatore */
  ESC: string;
  /** Ente competente */
  ECP: string;
  /**  Ente proponente */
  EPR: string;
  /** Codice univoco */
  NCT: NCT;
  /** Cronologia */
  DT: DT[];
  /** Compilazione */
  CMP: CMP;
  /** Oggetto */
  OGT: OGT;
  /** Soggetto */
  SGT: SGT;
  /** Descrizione del punto */
  DES: DES;
  /** Materia e tecnica */
  MTC: string[];
  /** Autore */
  AUT: AUT[];
  /** Acquisizione */
  ACQ: ACQ;
  /** Condizione giuridica */
  CDG: CDG;
  /** Aggiornamento / Revisione */
  AGG: AGG[];
  /** Altre localizzazioni */
  LA: LA[];
  /** Osservazioni */
  OSS: string;
  /** Stato di conservazione */
  STC: STC;
}

export interface Product {
  prezzoDiListino: number;
  prezzoDiVendita: number;
  iva: number;
}

export interface LinkWeb {
  descrizione: string;
  tipo: string;
  titolo: string;
  url: string;
}

export interface Card extends ICCD {
  id: string | '';
  codiceUnico: string;
  user?: string;
  previewUrl?: string;
  prodotto?: Product;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  status: Status;
  progetti: string[];
  collegamentiWeb: LinkWeb[];
  note: string;
}

export interface CardRelation extends Card {
  fontiArc: SourceArc[];
  fontiBib: SourceBib[];
  movimenti: Movement[];
  relazioniBeniOA?: Array<Relation & { beneOA: string }>;
  relazioniBeniS?: Array<Relation & { beneS: string }>;
  relazioniBeniF?: Array<Relation & { beneF: string }>;
  relazioniBeniD?: Array<Relation & { beneD: string }>;
  relazioniBeniMI?: Array<Relation & { beneD: string }>;
  restauri: Restoration[];
  allegati: Attachment[];
}

export interface CardPaper extends CardRelation {
  /** Filigrana */
  FIL: string;
  /** Forma */
  FRM: string;
}

export const NCTMeta: FieldsOptions<NCT> = {
  NCTN: {value: ''},
  NCTR: {value: ''},
};

export const CMPMeta: FieldsOptions<CMP> = {
  CMPD: {value: ''},
  CMPN: {value: ''},
};

export const DTSMeta: FieldsOptions<DTS> = {
  DTSF: {value: ''},
  DTSI: {value: ''},
};

// FIXME: Why DTS interface does not work
export const DTMeta: FieldsOptions<any> = {
  DTM: {value: [], type: 'array'},
  DTS: {value: {}, controls: DTSMeta, type: 'object'},
};

export const DESMeta: FieldsOptions<DES> = {
  DESO: {},
};

export const SGTMeta: FieldsOptions<SGT> = {
  SGTI: {},
  SGTT: {},
};

export const OGTMeta: FieldsOptions<OGT> = {
  OGTD: {},
};

export const AUTMeta: FieldsOptions<AUT> = {
  AUTA: {},
  AUTN: {},
};

export const ACQMeta: FieldsOptions<ACQ> = {
  ACQD: {},
  ACQL: {},
  ACQN: {},
  ACQT: {},
};

export const AGGMeta: FieldsOptions<AGG> = {
  AGGD: {},
  AGGN: {},
};

export const CDGMeta: FieldsOptions<CDG> = {
  CDGG: {},
  CDGI: {},
  CDGS: {},
};

export const PRDMeta: FieldsOptions<PRD> = {
  PRDI: {value: '', validators: [iccdDateValidator()]},
  PRDU: {value: '', validators: [iccdDateValidator()]},
};

export const PRVMeta: FieldsOptions<PRV> = {
  PRVC: {},
  PRVE: {},
  PRVL: {},
  PRVP: {},
  PRVR: {},
  PRVS: {},
};

// FIXME: Why LA interface does not work
export const LAMeta: FieldsOptions<any> = {
  PRD: {type: 'object', controls: PRDMeta, value: {}},
  PRV: {type: 'object', controls: PRVMeta, value: {}},
  TCL: {},
};

export const STCMeta: FieldsOptions<STC> = {
  STCC: {},
  STCS: {},
};

// FIXME: Why ICCD interface does not work
export const ICCDMeta: FieldsOptions<any> = {
  ACQ: {controls: ACQMeta, type: 'object', value: {}, additionalInfo: {label: 'Acquisizione'}},
  AGG: {controls: AGGMeta, type: 'array', value: {}, additionalInfo: {label: 'Aggiornamento / Revisione'}},
  AUT: {controls: AUTMeta, type: 'array', value: [], additionalInfo: {label: 'Autore'}},
  CDG: {controls: CDGMeta, type: 'object', value: {}, additionalInfo: {label: 'Condizione giuridica'}},
  CMP: {controls: CMPMeta, type: 'object', value: {}, additionalInfo: {label: 'Compilazione'}},
  DES: {controls: DESMeta, type: 'object', value: {}, additionalInfo: {label: 'Descrizione'}},
  DT: {controls: DTMeta, type: 'array', value: [], additionalInfo: {label: 'Cronologia'}},
  ECP: {value: '', additionalInfo: {single: true}},
  EPR: {value: '', additionalInfo: {single: true}},
  ESC: {value: 'Atelier Mitoraj', additionalInfo: {single: true}},
  LA: {type: 'array', controls: LAMeta, value: [], additionalInfo: {label: 'Localizzazioni'}},
  LIR: {value: 'I', additionalInfo: {single: true}},
  MTC: {type: 'array', value: [], additionalInfo: {label: 'Materia e Tecnica'}},
  NCT: {controls: NCTMeta, type: 'object', value: {}, additionalInfo: {label: 'Codici'}},
  NSC: {value: '', additionalInfo: {single: true}},
  OGT: {controls: OGTMeta, type: 'object', value: {}, additionalInfo: {label: 'Oggetto'}},
  OSS: {value: '', additionalInfo: {single: true}},
  SGT: {controls: SGTMeta, type: 'object', value: {}, additionalInfo: {label: 'Soggetto'}},
  STC: {value: {}, type: 'object', controls: STCMeta, additionalInfo: {label: 'Stato di conservazione'}},
  TSK: {value: '', validators: [Validators.required], additionalInfo: {single: true}, disabled: true},
};

export const ProductMeta: FieldsOptions<Product> = {
  iva: {},
  prezzoDiListino: {},
  prezzoDiVendita: {},
};

export const LinKWebMeta: FieldsOptions<LinkWeb> = {
  descrizione: {value: ''},
  tipo: {value: ''},
  titolo: {value: ''},
  url: {value: '', validators: Validators.required},
};

// FIXME: Why Card interface does not work
export const CardMeta: FieldsOptions<any> = {
  ...ICCDMeta,
  codiceUnico: {value: '', validators: Validators.required},
  collegamentiWeb: {value: [], type: 'array', controls: LinKWebMeta},
  createdAt: {value: new Date()},
  note: {value: ''},
  prodotto: {value: {}, controls: ProductMeta, type: 'object'},
  progetti: {value: [], type: 'array'},
  status: {value: Status.STATUS_DRAFT},
  updatedAt: {},
  user: {value: ''},
};

// FIXME: Why CardRelation interface does not work
export const CardRelationMeta: FieldsOptions<any> = {
  ...CardMeta,
  allegati: {type: 'array', value: [], controls: AttachmentMeta},
  fontiArc: {type: 'array', value: [], controls: SourceArcMeta},
  fontiBib: {type: 'array', value: [], controls: SourceBibMeta},
  movimenti: {type: 'array', value: [], controls: MovementMeta},
  restauri: {type: 'array', value: [], controls: RestorationMeta},
};

export const CardPaperMeta: FieldsOptions<CardPaper> = {
  ...CardRelationMeta,
  FIL: {value: '', additionalInfo: {labeL: 'Filigrana', single: true}},
  FRM: {value: '', additionalInfo: {label: 'Forma', single: true}},
};
